import PortalLearningPath from '@classes/LearningPath'
import PortalLearningPathUnit from '@classes/LearningPathUnit'
import Collapse from '@components/Collapse'
import EmptyLearningPathV3 from '@components/EmptyLearningPathV3'
import CollapseHeader from '@layouts/LearningPath/Layout/CollapseHeader'
import ProgressNav from '@layouts/LearningPath/Layout/ProgressNav'
import { MaintenanceBannerContainer, TileRow } from '@layouts/LearningPath/Layout/style'
import ProgramContent from '@layouts/LearningPath/ProgramContent'
import React from 'react'
import PanelTile from '@layouts/LearningPath/Layout/PanelTile'
import GenericErrorBoundary from '@hocs/ErrorBoundary/GenericErrorBoundary'
import Spacer from '@berlitz/spacer'
import { StudentLanguageMaterial } from '@interfaces/StudentLanguageMaterial'
import { getSkillsTutorLevelLabelFromLevel } from '@features/skillsTutor/utils'

export interface SkillsTutorPanelPathProps {
  Path: PortalLearningPath | undefined
  SkillsTutorMaterial?: StudentLanguageMaterial
}

/**
 * SkillsTutorPanelPath
 *
 * This component is specifically designed for Skills Tutor which is now based on
 * student profile languages rather than registrations.
 */

const SkillsTutorPanelPath: React.FC<SkillsTutorPanelPathProps> = ({ Path, SkillsTutorMaterial }) => {
  // skills level to display the level badge on learning path header
  const skillsTutorLevel = getSkillsTutorLevelLabelFromLevel(Path?.Level?.[0])

  const sanitizeTitle = (title: string | undefined) => (title ? title.replace(/\s[0-9]{1,2}$/, '').trim() : '')

  // Skills Tutor doesn't use modules, so using all NonCultureUnits
  const Units = Path?.NonCultureUnits

  return (
    <GenericErrorBoundary
      fallback={
        <Collapse.Panel
          key={Path?.Id}
          id={Path?.Id?.toString() || 'st-error-boundary'}
          header={
            <CollapseHeader
              title={sanitizeTitle(Path?.skillsTutorMaterial?.[0]?.MaterialName)}
              language={Path?.Language || ''}
              isFlex={Boolean(Path?.IsFlex)}
              isSkillTutorPath={Boolean(Path?.IsSkillTutorContent) || true}
              level={skillsTutorLevel}
            />
          }
        >
          <MaintenanceBannerContainer>
            <EmptyLearningPathV3 />
          </MaintenanceBannerContainer>
        </Collapse.Panel>
      }
    >
      {Path ? (
        <Collapse.Panel
          key={Path?.Id}
          id={Path?.Id?.toString() || 'st-path-error-boundary'}
          header={
            <CollapseHeader
              title={sanitizeTitle(Path?.ProgramName)}
              language={Path.Language}
              path={Path}
              isSkillTutorPath={Boolean(Path?.IsSkillTutorContent) || true}
              level={skillsTutorLevel}
            />
          }
        >
          <>
            <ProgramContent Path={Path} />
            <TileRow id={`scroller-${Path.Id}`}>
              {Units?.map((Unit) => {
                if (Unit.IsCoaching) {
                  Unit.Unlimited = Path.UnlimitedCoaching
                }
                return <PanelTile Unit={Unit as PortalLearningPathUnit} Path={Path} key={Unit?.Id} />
              })}
            </TileRow>
            <ProgressNav Units={Units!} scrollerId={`scroller-${Path.Id}`} />
          </>
        </Collapse.Panel>
      ) : (
        <Collapse.Panel
          key={SkillsTutorMaterial?.MaterialId || ''}
          id={SkillsTutorMaterial?.MaterialId || ''}
          header={
            <CollapseHeader
              title={sanitizeTitle(SkillsTutorMaterial?.MaterialName)}
              language={SkillsTutorMaterial?.StudentLanguageProfileLanguage}
              isSkillTutorPath={true}
            />
          }
        >
          <MaintenanceBannerContainer>
            <EmptyLearningPathV3 />
          </MaintenanceBannerContainer>
          <Spacer size="lg" />
        </Collapse.Panel>
      )}
    </GenericErrorBoundary>
  )
}

export default React.memo(SkillsTutorPanelPath)
